import axios from 'axios';
import { apiClient } from '@/lib/apiClient';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

export type LoginResult =
  | { kind: 'ok'; accessToken: string; role: string }
  | { kind: '2fa' };

/**
 * Login (plain axios, no admin header). Returns kind:'2fa' when the account has two-factor enabled and
 * a code is required — the caller then re-submits with the authenticator code. Throws on bad credentials.
 */
export const login = async (email: string, password: string, twoFactorCode?: string): Promise<LoginResult> => {
  try {
    const { data } = await axios.post<{ accessToken: string; role: string }>(
      `${BASE_URL}/auth/login`, { email, password, twoFactorCode }, { withCredentials: true },
    );
    return { kind: 'ok', ...data };
  } catch (e) {
    const resp = (e as { response?: { status?: number; data?: { requiresTwoFactor?: boolean } } }).response;
    if (resp?.status === 401 && resp.data?.requiresTwoFactor) return { kind: '2fa' };
    throw e;
  }
};

export const logout = () => axios.delete(`${BASE_URL}/auth/refresh`, { withCredentials: true });

export interface Dashboard {
  tenantCount: number;
  activeCount: number;
  trialingCount: number;
  pastDueCount: number;
  estimatedMrr: number;
  currency: string;
  totalCabinets: number;
  totalUsers: number;
  totalPatients: number;
  byStatus: { status: string; count: number }[];
}

export const getDashboard = async () => (await apiClient.get<Dashboard>('/admin/dashboard')).data;

export interface TenantRow {
  id: string;
  name: string;
  slug: string;
  planName: string;
  status: string;
  cabinets: number;
  users: number;
  patients: number;
  walletBalance: number;
  currency: string;
  isDemo: boolean;
  createdAt: string;
}

export const listTenants = async () => (await apiClient.get<{ data: TenantRow[] }>('/admin/tenants')).data.data;

export interface TenantDetail extends Omit<TenantRow, 'planName' | 'status'> {
  countryCode: string;
  // Offboarding. Without these the console shows "Suspended" and nothing else — no sign that a
  // purge is scheduled, when it lands, or that it already happened.
  lapsedAt: string | null;
  purgeScheduledAt: string | null;
  purgeWarnedAt: string | null;
  dataPurgedAt: string | null;
  dataRetentionGraceDays: number;
  planName: string;
  status: string;
  trialEndsAt: string;
  subscriptionEndsAt: string | null;
  isAnnualBilling: boolean;
  hasStripeCustomer: boolean;
  hasStripeSubscription: boolean;
  connectOnboarded: boolean;
  connectChargesEnabled: boolean;
  storageMode: string;
  storageQuotaMb: number;
  /** The plan's normal price. */
  listMonthlyPrice: number;
  /** What this clinic actually pays, discount and any running promotion applied. */
  effectiveMonthlyPrice: number;
  discountPercent: number;
  discountAmount: number;
  discountUntil: string | null;
  discountReason: string | null;
}

export const getTenant = async (id: string) => (await apiClient.get<TenantDetail>(`/admin/tenants/${id}`)).data;
export const suspendTenant = (id: string) => apiClient.post(`/admin/tenants/${id}/suspend`);
export const activateTenant = (id: string) => apiClient.post(`/admin/tenants/${id}/activate`);

/**
 * Hard-deletes a clinic's data. Irreversible.
 *
 * `confirmation` must be the clinic's exact name — the API refuses anything else. A tenant id in a
 * URL is easy to paste wrong; a clinic's name is not something typed by accident.
 */
export const purgeTenant = (id: string, confirmation: string) =>
  apiClient.post(`/admin/tenants/${id}/purge`, { confirmation });

/** Percent or amount, never both. Zero for both clears the discount. */
export const setTenantDiscount = (
  id: string,
  body: { percent: number; amount: number; until: string | null; reason: string | null },
) => apiClient.put(`/admin/tenants/${id}/discount`, body);

export interface AdminPlan {
  id: string;
  name: string;
  type: string;
  monthlyPrice: number;
  annualPrice: number;
  currency: string;
  maxCabinets: number;
  maxSeats: number;
  maxDoctors: number;
  storageQuotaMb: number;
  isActive: boolean;
  stripePriceIdMonthly: string | null;
  stripePriceIdAnnual: string | null;
  /** Null = no promotion. While set, this is the price the public site advertises. */
  promoMonthlyPrice: number | null;
  promoAnnualPrice: number | null;
  /** Last day of the offer, inclusive. Null = until it is removed. */
  promoUntil: string | null;
  /** Your own wording for the badge, instead of the computed "X% off". */
  promoLabel: string | null;
}

export const listPlans = async () => (await apiClient.get<{ data: AdminPlan[] }>('/admin/plans')).data.data;

export const updatePlan = (id: string, body: Omit<AdminPlan, 'id' | 'type' | 'currency'>) =>
  apiClient.put(`/admin/plans/${id}`, body);

// ── Public-site demand (§7.19) ──

/** One roadmap item and how many people have asked for it. */
export interface FeatureDemandRow {
  slug: string;
  itemKey: string;
  votes: number;
  /** People who left an address about this feature specifically. */
  subscribers: number;
  lastVoteAt: string | null;
}

export interface FeatureDemand {
  items: FeatureDemandRow[];
  /** Addresses waiting on anything at all, rather than a named feature. */
  siteWideSubscribers: number;
  totalSubscribers: number;
  totalVotes: number;
}

/**
 * What visitors to the public site have asked us to build, most-wanted first.
 *
 * Counts only — the addresses behind them are deliberately not returned. Deciding what to build
 * needs the number, not the list.
 */
export const getFeatureDemand = async () =>
  (await apiClient.get<FeatureDemand>('/admin/feature-demand')).data;

// ── Suppliers & ad creatives (§7.17) ──
export interface SupplierRow {
  id: string;
  name: string;
  email: string;
  contactName: string | null;
  status: string;
  walletBalance: number;
  currency: string;
  pendingCreatives: number;
}
export const listSuppliers = async () => (await apiClient.get<{ data: SupplierRow[] }>('/admin/suppliers')).data.data;
export const createSupplier = (body: { name: string; email: string; contactName?: string; password: string }) =>
  apiClient.post('/admin/suppliers', body);
export const setSupplierStatus = (id: string, status: string) => apiClient.post(`/admin/suppliers/${id}/status`, { status });
export const fundSupplier = (id: string, amount: number) => apiClient.post(`/admin/suppliers/${id}/fund`, { amount });

export interface CreativeRow {
  id: string;
  supplierId: string;
  supplierName: string;
  title: string;
  mediaType: string;
  imageUrl: string;
  targetUrl: string;
  status: string;
  reviewNote: string | null;
  createdAt: string;
}
export const listCreatives = async (status = 'Pending') =>
  (await apiClient.get<{ data: CreativeRow[] }>('/admin/creatives', { params: { status } })).data.data;
export const reviewCreative = (id: string, approve: boolean, note?: string) =>
  apiClient.post(`/admin/creatives/${id}/review`, { approve, note });

// ── Platform settings ────────────────────────────────────────────────────────
// Stored in the database rather than config so they take effect on the next send,
// with no restart and no deploy.

export interface PlatformSettings {
  opsEmail: string | null;
  /** What an imaging centre pays per delivered study. Zero means referrals are free. */
  imagingStudyFee: number;
  /** When imaging billing began — a fee set later can never reach back over earlier work. */
  imagingBillingSince: string | null;
  /** Monthly price of one storage add-on block. Zero means add-on storage is free. */
  storageAddOnPrice: number;
  storageAddOnBlockMb: number;
}

export const getPlatformSettings = async () =>
  (await apiClient.get<PlatformSettings>('/admin/platform-settings')).data;

/**
 * Omitted prices are left alone. Null and zero mean different things here — sending zero for a price
 * you did not mean to edit would give that stream away.
 */
export const updatePlatformSettings = (body: {
  opsEmail: string | null;
  imagingStudyFee?: number;
  storageAddOnPrice?: number;
}) => apiClient.put('/admin/platform-settings', body);

// ── The operator's own company ────────────────────────────────────────────────
//
// The seller party on every invoice easyDent issues. Kept apart from platform settings because it
// is one correlated record — a legal name saved without a tax id produces invoices the tax
// authority rejects, so completeness is reported rather than assumed.

export interface PlatformCompany {
  legalName: string;
  tradingName: string | null;
  countryCode: string;
  taxId: string;
  registrationNumber: string;
  address: string;
  city: string;
  postalCode: string | null;
  /** ISO 3166-2, e.g. RO-CJ — BT-39 on an e-invoice. Stated, never inferred from the city. */
  countySubdivision: string;
  iban: string | null;
  bankName: string | null;
  email: string;
  phone: string | null;
  isVatRegistered: boolean;
  defaultVatRatePercent: number;
  defaultCurrency: string;
  invoiceSeries: string;
  paymentTermsDays: number;
  /** Whether this company can yet be named as the seller on a compliant e-invoice. */
  hasFiscalIdentity: boolean;
  /** Which required fields are still blank, so the screen can say which. */
  missingFields: string[];
}

export type PlatformCompanyInput = Omit<PlatformCompany, 'hasFiscalIdentity' | 'missingFields'>;

/** Null when nothing has been set up yet. */
export const getPlatformCompany = async () =>
  (await apiClient.get<PlatformCompany | null>('/admin/platform-company')).data;

export const updatePlatformCompany = async (body: PlatformCompanyInput) =>
  (await apiClient.put<PlatformCompany>('/admin/platform-company', body)).data;
// ── Customers we cannot yet invoice ──────────────────────────────────────────
//
// The one part of billing that waits on other people. Visible here rather than discovered when a
// billing run produces documents that cannot be filed.

export interface IncompleteCustomer {
  customerType: 'Tenant' | 'Supplier' | 'ImagingCentre';
  customerId: string;
  name: string;
  email: string | null;
  /** Named, so a chasing email can say exactly what is wanted. */
  missingFields: string[];
  /** Money has already moved for this one — the urgent ones. */
  hasBalance: boolean;
}

export const getIncompleteCustomers = async () =>
  (await apiClient.get<{ data: IncompleteCustomer[] }>('/admin/incomplete-customers')).data.data;

// ── The invoices easyDent issues ─────────────────────────────────────────────
//
// Drafting and issuing are separate calls on purpose: a draft can be redrawn as often as you like,
// while issuing claims a fiscal number, and a gap in a numbered series is the first thing an
// auditor asks about.

export type PlatformInvoiceStatus =
  | 'Draft' | 'AwaitingCustomerDetails' | 'Issued' | 'Submitted'
  | 'Filed' | 'Rejected' | 'Failed' | 'Cancelled';

export interface PlatformInvoiceLine {
  stream: string;
  description: string;
  net: number;
  vatRatePercent: number;
  vat: number;
  /** Belongs to an earlier period — picked up here rather than restating a document already sent. */
  isLatePosting: boolean;
}

export interface PlatformInvoiceRow {
  id: string;
  customerType: 'Tenant' | 'Supplier' | 'ImagingCentre';
  customerId: string;
  buyerName: string;
  buyerTaxId: string | null;
  /** "EDF-0042", or "Draft" before a number has been claimed. */
  reference: string;
  status: PlatformInvoiceStatus;
  kind: 'Invoice' | 'CreditNote';
  periodStart: string;
  periodEnd: string;
  issuedAt: string | null;
  dueOn: string | null;
  currency: string;
  netTotal: number;
  vatTotal: number;
  grossTotal: number;
  vatTreatment: 'NotVatRegistered' | 'Standard' | 'ReverseCharge' | 'Exempt';
  /** Which fields the customer has not supplied, when that is what is blocking it. */
  blockedReason: string | null;
  hasPdf: boolean;
  /** "Anaf" or "Generic" — decided by the buyer's country, not ours. */
  provider: string | null;
  error: string | null;
  hasXml: boolean;
  lines: PlatformInvoiceLine[];
}

export const listPlatformInvoices = async (params?: { customerType?: string; customerId?: string }) =>
  (await apiClient.get<{ data: PlatformInvoiceRow[] }>('/admin/platform-invoices', { params })).data.data;

export const getPlatformInvoice = async (id: string) =>
  (await apiClient.get<PlatformInvoiceRow>(`/admin/platform-invoices/${id}`)).data;

export const issuePlatformInvoice = (id: string) =>
  apiClient.post(`/admin/platform-invoices/${id}/issue`);

/** Send it to the tax authority now instead of waiting for the hourly sweep. */
export const filePlatformInvoice = (id: string) =>
  apiClient.post(`/admin/platform-invoices/${id}/file`);

export const cancelPlatformInvoice = (id: string, reason: string) =>
  apiClient.post(`/admin/platform-invoices/${id}/cancel`, { reason });

/**
 * Correct an invoice that has already gone out. Omit the amount to credit the whole thing.
 *
 * The only way to correct one: an issued document has a fiscal number, may have been filed, and the
 * customer's accountant has a copy. Deleting it would leave a gap in a numbered series.
 */
export const creditPlatformInvoice = (id: string, reason: string, amount?: number) =>
  apiClient.post<{ id: string }>(`/admin/platform-invoices/${id}/credit`, { reason, amount: amount ?? null });

/** The e-Factura XML as it was sent — the only thing worth reading against a rejection. */
export const getPlatformInvoiceXml = async (id: string) =>
  (await apiClient.get<string>(`/admin/platform-invoices/${id}/xml`, { responseType: 'text' })).data;

// ── The monthly run ──────────────────────────────────────────────────────────

export interface PlatformBillingRun {
  id: string;
  periodStart: string;
  periodEnd: string;
  startedAt: string;
  completedAt: string | null;
  customersConsidered: number;
  invoicesDrafted: number;
  /** Drafted but unissuable — the customer has not given us their fiscal details. */
  invoicesBlocked: number;
  failures: number;
  error: string | null;
}

export const listBillingRuns = async () =>
  (await apiClient.get<{ data: PlatformBillingRun[] }>('/admin/platform-invoices/runs')).data.data;

export const runPlatformBilling = (periodStart: string, periodEnd: string) =>
  apiClient.post('/admin/platform-invoices/runs', { periodStart, periodEnd });

// ── Our own e-Factura enrolment ──────────────────────────────────────────────
//
// The one step that cannot be automated: somebody at the operating company presents a qualified
// certificate to ANAF in a browser, once. The certificate never reaches easyDent — what comes back
// is an ordinary OAuth code, and the refresh token does the rest for about a year.

export interface PlatformFiscalEnrolment {
  enrolled: boolean;
  /** False until easyDent has an application registered with ANAF — not something clicking fixes. */
  platformReady: boolean;
  cif: string | null;
  environment: 'test' | 'prod';
  accessTokenExpiresAt: string | null;
  refreshTokenExpiresAt: string | null;
  lastRefreshedAt: string | null;
  lastError: string | null;
  canFile: boolean;
}

export const getPlatformEnrolment = async () =>
  (await apiClient.get<PlatformFiscalEnrolment>('/admin/platform-fiscal/enrolment')).data;

/** Returns the ANAF URL where the certificate is presented. */
export const beginPlatformEnrolment = async (cif: string, environment: string, redirectUri: string) =>
  (await apiClient.post<{ authorizeUrl: string }>(
    '/admin/platform-fiscal/enrolment/begin', { cif, environment, redirectUri })).data.authorizeUrl;

export const completePlatformEnrolment = (code: string, redirectUri: string) =>
  apiClient.post('/admin/platform-fiscal/enrolment/complete', { code, redirectUri });

export const disablePlatformEnrolment = () =>
  apiClient.delete('/admin/platform-fiscal/enrolment');

// ── Password reset ───────────────────────────────────────────────────────────
// Plain axios, not apiClient: both are used by someone who is NOT signed in, so there is no token to
// attach and no admin header to send.

/**
 * Asks for a reset link. Resolves the same way whether or not the address is known — the API says
 * nothing either, so that a caller cannot use this to discover which addresses hold accounts.
 */
export const forgotPassword = (email: string) =>
  axios.post(`${BASE_URL}/auth/forgot-password`, { email });

/** Consumes an emailed token. 404 = spent or expired link; 400 = the password was refused. */
export const resetPassword = (token: string, newPassword: string) =>
  axios.post(`${BASE_URL}/auth/reset-password`, { token, newPassword });

// ── Operators ────────────────────────────────────────────────────────────────

export interface OperatorSummary {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  hasPassword: boolean;
  twoFactorEnabled: boolean;
  lastSeenAt: string | null;
  createdAt: string;
}

export const listOperators = async () =>
  (await apiClient.get<OperatorSummary[]>('/admin/operators')).data;

/** No password argument by design — they are emailed a link and choose their own. */
export const createOperator = async (email: string, firstName: string, lastName: string) =>
  (await apiClient.post<OperatorSummary>('/admin/operators', { email, firstName, lastName })).data;

export const removeOperator = (id: string) => apiClient.delete(`/admin/operators/${id}`);
